// Margin of Error hover overlay — Highcharts-native port of the old D3 implementation.
// Ported from highchart_models/base.coffee (getMode, getSigTest, getConfidenceLevel,
// pointMouseOver/Out, _pointClick, _pointStateRecalc) + columnbar.coffee event wiring.
//
// P1 scope: state machine (S1–S5, S8) + sig-test/mode helpers + wireMoEEvents.
// Visual render functions (_show/_hide/_recalc) are STUBS — filled in P2.

import statsMath from '@aytm/app/stats/lib/util/math'
import { hexToHsl, transformHex } from '@aytm/app/stats/lib/render/colors'

import { getConfidenceInterval } from '@aytm/app/stats/page/question-views/components/charts/highcharts/lib/errData'
import PageGlobals from '@aytm/app/stats/lib/page/page-globals'

const { sigTest, sigTestMeanTwoSample } = statsMath

const DEFAULT_FORMAT = { showErrors: true, showSignificance: true }

// Visual constants — mirror highchart_models/base.coffee:_pointMouseOver.
const CI_BAND_ID = 'moe-ci-band'
const CI_LINE_ID = 'moe-ci-line'
// The band lies over the columns rather than behind them, and multiplies rather than covers: a
// solid ink through multiply darkens what it crosses instead of washing it out, so a column keeps
// its own colour and its hatching through the interval the way a highlighter leaves text readable.
// Alpha would only fade both together, which is what made the band have to hide behind the columns.
const CI_BAND_COLOR = '#c9ebe1'
const CI_LINE_COLOR = '#29ABE2'

// Above the series (3), below the data labels (6) so the figures stay clear of the tint, and below
// the MoE legend (7) and the tooltip (8). The lines take the step above the band rather than sharing
// it: Highcharts buckets bands and lines into sibling groups per zIndex and breaks a tie by insertion
// order, so on equal z whichever was hovered first ends up on top and the band can paint over a line.
const CI_BAND_Z = 4
const CI_LINE_Z = 5

// Both marks are drawn through a class rather than by reaching for the rendered element: Highcharts
// keeps a plot band's *options* and rebuilds it from them on any axis update, which would drop
// anything set on the element afterwards. What the class carries is in highcharts.scss.
const CI_BAND_CLASS = 'aytm-moe-band'
const CI_LINE_CLASS = 'aytm-moe-ci-line'
// The significance mark is drawn as clones of the hovered column and its non-significant peers, and
// the clones carry this so the mark can be found without reading fills; a point whose answer carries
// no interval (a Correlation Finder's) draws this mark alone.
const SIG_CLASS = 'aytm-moe-sig'

// Weight and spacing of the significance hatch, stated rather than written into the pattern, so the
// mark can be read off two numbers instead of four coordinates. Finer and lighter than the mark it
// replaces: at a 10px pitch a 4px stroke covered nearly half of what it crossed, which read as a
// second fill over the column rather than as shading laid on the one already there.
const HATCH_PITCH = 5
const HATCH_STROKE = 1.25

const STRIPE_COLOR = 'rgba(0, 0, 0, 0.2)'
const LEGEND_FONT = 'Ubuntu, Calibri, Segoe, Helvetica, Arial'

// Module-local counter for unique SVG pattern ids (one stripe pattern per chart instance).
let moeSeq = 0

// 'percents' | answerType — mirrors base.coffee:245-258 (getMode).
export const getMode = config => {
  const { answerType = 'amount', ignorePercents, axisType, chartMode } = config || {}
  const percentsMode = axisType ? axisType === 'percents' : !ignorePercents && (chartMode || '').endsWith('percents')
  return percentsMode ? 'percents' : answerType
}

// Are two points statistically significantly different? Mirrors base.coffee:346-358.
// Three paths: precomputed backend sigtest payload, mean two-sample t-test (scale), proportion test.
export const getSigTest = (p1, p2, config) => {
  const confidenceLevel = config.confidenceLevel
  const conf = String(1000 * confidenceLevel)

  // Enter the pre-computed branch when either bar carries sigtest data: the backend stores each
  // significant pair only on the higher-mean bar, so the lower-mean bar has no entry. The
  // two-direction lookup below then decides symmetrically (p1 lists p2 OR p2 lists p1).
  if (Object.hasOwn(p1.errData, 'sigtest') || Object.hasOwn(p2.errData, 'sigtest')) {
    const key = `${conf}_${conf}`
    let res = p1.errData.sigtest?.[key]?.length > 0 && p1.errData.sigtest[key].some(st => st.i === p2.errData.i)
    res ||= p2.errData.sigtest?.[key]?.length > 0 && p2.errData.sigtest[key].some(st => st.i === p1.errData.i)
    return res
  }

  if (Object.hasOwn(p1.errData, 'sd')) {
    return sigTestMeanTwoSample(
      p1.errData.mu,
      p2.errData.mu,
      p1.errData.sd,
      p2.errData.sd,
      p1.errData.total,
      p2.errData.total,
      confidenceLevel
    )
  }

  // 1:1 with old code: proportion path reads errData.amount (valid only when answerType==='amount').
  return sigTest(p1.errData.amount, p2.errData.amount, p1.errData.total, p2.errData.total, 1000 - Number(conf))
}

// Legend row 2 text — mirrors base.coffee:294-297 (getConfidenceLevel).
export const getConfidenceLevelText = config => {
  const noun = ['bars', 'stacked-bars'].includes(config.qType) ? 'Bars' : 'Answers'
  const found = (PageGlobals.CONFIDENCE_LEVELS || []).find(v => v[0] === config.confidenceLevel)
  const level = found ? found[1] : ''
  return `${noun} which are not statistically significantly different at the ${level} confidence level`
}

// Default non-sig selection (answer-variants + dataLab interlaced): all series, same point index.
// Mirrors base.coffee:467-472. dataLab 'grouped' overrides this via params (P5).
export const selectNonSigDefault = (chart, hovered, config) => {
  const result = []
  for (const series of chart.series) {
    if (!series.visible) continue // legend-hidden series must not get a stripe at the hidden bar
    const other = series.points?.[hovered.index]
    if (other && other.id !== hovered.id && !getSigTest(hovered, other, config)) result.push(other)
  }
  return result
}

// ─── dataLab parameters (passed as props.moeParams) ───

// dataLab getMode: respondents axis → answerType, otherwise percents. Mirrors dataLabColumnsConfig.js:22.
export const getModeDataLab = config => (config.axisType === 'respondents' ? config.answerType || 'amount' : 'percents')

// dataLab non-sig selection depends on groupType (data_lab/columns.coffee:131-137):
//   grouped    → same series, ALL its points  (series = answer variant, points = batches)
//   interlaced → ALL series, same point index (series = batches, points = answer variants)
// Both mean "same answer variant across batches" (errData.i = batch index).
export const selectNonSigDataLab = (chart, hovered, config) => {
  const candidates =
    config.groupType === 'grouped'
      ? chart.series[hovered.series.index]?.points || []
      : chart.series.filter(series => series.visible).map(series => series.points?.[hovered.index])
  return candidates.filter(p => p && p.id !== hovered.id && !getSigTest(hovered, p, config))
}

// Params bundle for dataLab columns/bars charts (stable module const → safe for useRef).
export const DATALAB_MOE_PARAMS = {
  getModeFn: getModeDataLab,
  selectNonSig: selectNonSigDataLab,
  dimOthers: true
}

// Stateful MoE controller — one per chart, lives outside React (created in useMarginOfError).
// Direct analogue of the old Backbone model instance holding pointOver/pointState/pointTimer.
// `params` lets answer-variants and dataLab share the engine while differing in:
//   getModeFn, selectNonSig (grouped vs interlaced), dimOthers, patternId.
export const createMoEController = (params = {}) => {
  const { getModeFn = getMode, selectNonSig = selectNonSigDefault, dimOthers = false } = params

  return {
    config: {},
    pointOver: null,
    pointState: null,
    pointTimer: null,
    pointDuration: 100,
    legendGroup: null,
    chart: null, // chart instance captured on show, used for cleanup in destroy()
    overlayEls: [], // renderer elements (significance clones + legend) to destroy on hide
    dimmed: [], // points dimmed to 0.3 (dataLab) to restore on hide
    patternId: params.patternId || `moe-stripes-${(moeSeq += 1)}`,
    getMode: getModeFn,
    selectNonSig,
    dimOthers,

    // Show conditions — mirrors pointMouseOver/Out/Click guards (base.coffee:_resolveHoverFormat).
    // Returns the format the overlay should render with, or null when the point cannot drive a
    // hover at all. Matrix score views carry sigtest data but no per-confidence-level err data —
    // those still hover, with the CI band switched off.
    resolveFormat(point) {
      const format = { ...DEFAULT_FORMAT, ...(this.config.format || {}) }
      if (!format.showErrors && !format.showSignificance) return null
      if (!point || !point.errData) return null

      const hasSigtest = Object.hasOwn(point.errData, 'sigtest') || Object.hasOwn(point.errData, 'sd')

      const conf = String(1000 * this.config.confidenceLevel)
      const hasErr = point.errData.err?.low?.[conf] != null && point.errData.err?.high?.[conf] != null

      if (!hasErr) {
        format.showErrors = false
        if (!format.showSignificance || !hasSigtest) return null
      }

      return format
    },

    // S1 (10ms debounce) + S2 (freeze while pinned).
    pointMouseOver(point) {
      if (this.pointState) return
      if (!this.resolveFormat(point)) return
      clearTimeout(this.pointTimer)
      this.pointTimer = setTimeout(() => {
        if (this.pointOver?.series) this._hide(this.pointOver)
        this.pointOver = point
        this._show(point)
      }, 10)
    },

    pointMouseOut(point) {
      if (this.pointState) return
      if (!this.resolveFormat(point)) return
      clearTimeout(this.pointTimer)
      this.pointTimer = setTimeout(() => {
        this._hide(point)
        this.pointOver = null
      }, 10)
    },

    // S3 — pin / unpin / switch. Mirrors _pointClick (base.coffee:678-690).
    pointClick(point) {
      if (!this.resolveFormat(point)) return
      this.pointOver = null
      if (this.pointState) {
        if (this.pointState.id === point.id) {
          this.pointOver = point // overlay stays as a hover, hides on next mouseOut
          this.pointState = null
        } else {
          this._hide(this.pointState)
          this.pointState = point
          this._show(point)
        }
      } else {
        this.pointState = point // overlay already on screen from the preceding hover
      }
    },

    // S4 — click on empty chart area clears the pin. Mirrors columnbar.coffee:9-12.
    backgroundClick() {
      if (this.pointState) {
        this._hide(this.pointState)
        this.pointState = null
      }
    },

    // S5 — legend clicks are blocked while pinned. Mirrors columnbar.coffee:21-24.
    legendItemClick(series, event) {
      if (this.pointState) {
        event?.preventDefault?.()
        return false
      }
      // TODO P2: active legend-item styling (columnbar.coffee:26-35)
      return
    },

    onLoad(chart) {
      this.ensurePattern(chart)
    },

    // S6/S7 + resize — re-render the pinned overlay against current data/config.
    // chart.update() (HighchartsReact, on every prop change) replaces Point objects, so the stored
    // pointState reference goes stale. Re-resolve it by id from the live chart before recalc; if the
    // pinned answer no longer exists (data changed / answer removed), drop the pin. Mirrors the
    // save-id / re-pin dance in highchart_view.coffee:43-49.
    onRedraw(chart) {
      if (!this.pointState) return
      const fresh = this._findPointById(chart || this.chart, this.pointState.id)
      if (fresh) {
        this.pointState = fresh
        this._recalc(fresh)
      } else {
        this._hide(this.pointState)
        this.pointState = null
      }
    },

    _findPointById(chart, id) {
      for (const series of chart?.series || []) {
        const found = (series.points || []).find(p => p.id === id)
        if (found) return found
      }
      return null
    },

    // S8 — cleanup on unmount/zoom.
    destroy() {
      clearTimeout(this.pointTimer)
      this.pointTimer = null
      if (this.pointOver) this._hide(this.pointOver)
      if (this.pointState) this._hide(this.pointState)
      this.pointOver = null
      this.pointState = null
    },

    // ─── Visual render ───

    // Diagonal-stripe SVG pattern, created once per chart (idempotent).
    // Built via direct DOM (not chart.renderer.definition): Highcharts 11's AST sanitizer strips
    // `patternTransform` (not in AST.allowedAttributes), which would kill the 45° rotation. Mirrors
    // the old D3 pattern (highchart_models/base.coffee:_pointMouseOver) exactly.
    ensurePattern(chart) {
      const defs = chart?.renderer?.defs?.element
      if (!defs) return
      chart.moePatterns = chart.moePatterns || {}
      if (chart.moePatterns[this.patternId]) return

      const NS = 'http://www.w3.org/2000/svg'
      const pattern = document.createElementNS(NS, 'pattern')
      pattern.setAttribute('id', this.patternId)
      pattern.setAttribute('patternUnits', 'userSpaceOnUse')
      pattern.setAttribute('patternTransform', 'rotate(45)')
      pattern.setAttribute('width', String(HATCH_PITCH))
      pattern.setAttribute('height', String(HATCH_PITCH))

      const line = document.createElementNS(NS, 'line')
      // centred in the tile, so weight and spacing can be read off the two constants alone
      line.setAttribute('x1', String(HATCH_PITCH / 2))
      line.setAttribute('y1', '0')
      line.setAttribute('x2', String(HATCH_PITCH / 2))
      line.setAttribute('y2', String(HATCH_PITCH))
      line.setAttribute('stroke', STRIPE_COLOR)
      line.setAttribute('stroke-width', String(HATCH_STROKE))

      pattern.appendChild(line)
      defs.appendChild(pattern)
      chart.moePatterns[this.patternId] = true
    },

    // animate=true on the initial hover/click show (fade-in overlay + legend); false on recalc
    // (resize / confidence change) so it re-renders without re-fading — mirrors legacy recalc.
    _show(point, animate = true) {
      const chart = point?.series?.chart
      if (!chart) return
      const format = this.resolveFormat(point)
      if (!format) return
      this.chart = chart

      if (format.showErrors) this._showCIBand(chart, point, animate)
      if (format.showSignificance) this._showSignificance(chart, point, animate)
      this._showLegend(chart, point, format, animate)

      // Hide the standard Highcharts legend while the MoE legend is shown, and make it inert:
      // a mid-hover click on the faded legend would hide a series and strand its painted bar.
      // Instant (legacy parity) — a fade window briefly stacks both legends in the same strip.
      chart.legend?.group?.attr?.({ opacity: 0 })
      this._setLegendInteractive(chart, false)
    },

    _hide(point) {
      const chart = point?.series?.chart || this.chart
      if (!chart) return
      chart.yAxis?.[0]?.removePlotBand?.(CI_BAND_ID)
      chart.yAxis?.[0]?.removePlotLine?.(CI_LINE_ID)
      this.overlayEls.forEach(el => el?.destroy?.())
      this.overlayEls = []
      this.dimmed.forEach(p => p?.graphic?.attr?.({ opacity: 1 }))
      this.dimmed = []
      this.legendGroup?.destroy?.()
      this.legendGroup = null
      chart.legend?.group?.animate?.({ opacity: 1 }, { duration: this.pointDuration })
      this._setLegendInteractive(chart, true)
    },

    _setLegendInteractive(chart, interactive) {
      const el = chart?.legend?.group?.element
      if (el) el.style.pointerEvents = interactive ? '' : 'none'
    },

    _recalc(point) {
      this._hide(point)
      this._show(point, false)
    },

    // CI band (full-width semi-transparent strip) + dashed centre line, in DATA coordinates.
    // Highcharts auto-handles inverted (bar) charts — no column/bar branch needed.
    _showCIBand(chart, point, animate) {
      const axis = chart.yAxis?.[0]
      if (!axis) return
      const conf = String(1000 * this.config.confidenceLevel)
      const mode = this.getMode(this.config)
      const errLow = point.errData.err.low[conf]?.[mode] || 0
      const errHigh = point.errData.err.high[conf]?.[mode] || 0
      const band = axis.addPlotBand({
        id: CI_BAND_ID,
        className: CI_BAND_CLASS,
        from: point.y - errLow,
        to: point.y + errHigh,
        color: CI_BAND_COLOR,
        zIndex: CI_BAND_Z
      })
      // inert like the legacy d3 band — grown over the bar tip it would steal the hover and
      // loop the point's mouseout/mouseover forever
      band?.svgElem?.css({ pointerEvents: 'none' })

      const line = axis.addPlotLine({
        id: CI_LINE_ID,
        className: CI_LINE_CLASS,
        value: point.y,
        color: CI_LINE_COLOR,
        dashStyle: 'Dash',
        width: 1,
        zIndex: CI_LINE_Z
      })
      line?.svgElem?.css({ pointerEvents: 'none' })
      // Grow the band from its centre line over pointDuration (legacy height-0→full effect) via a
      // scale+translate transform anchored at the band centre. The dashed line is the instant "seed"
      // the band grows from. scaleY for column, scaleX for bar (inverted). Skipped on recalc/resize.
      if (animate && band?.svgElem) {
        const el = band.svgElem
        const bb = el.getBBox()
        if (chart.inverted) {
          const cx = bb.x + bb.width / 2
          el.attr({ scaleX: 0, translateX: cx }).animate({ scaleX: 1, translateX: 0 }, { duration: this.pointDuration })
        } else {
          const cy = bb.y + bb.height / 2
          el.attr({ scaleY: 0, translateY: cy }).animate({ scaleY: 1, translateY: 0 }, { duration: this.pointDuration })
        }
      }
    },

    // Lighter-colour + diagonal-stripe overlay on the hovered point and every other point
    // that is NOT statistically significantly different from it.
    _showSignificance(chart, point, animate) {
      this.ensurePattern(chart)
      const points = [point, ...this.selectNonSig(chart, point, this.config)]

      // recorded as they are dimmed, not as they are brought back: the hide path restores this list,
      // and collecting the restored points instead left the faded ones faded until the next redraw
      if (this.dimOthers) {
        for (const series of chart.series) {
          for (const p of series.points || []) {
            if (!p.graphic) continue
            p.graphic.attr?.({ opacity: 0.3 })
            this.dimmed.push(p)
          }
        }
      }

      const base = point.color
      const hsl = typeof base === 'string' ? hexToHsl(base) : null
      const lighter = hsl ? transformHex(base, [0, 0, Math.min(20, (100 - hsl[2]) / 2)]) : base

      for (const p of points) {
        if (!p.graphic) continue
        if (this.dimOthers) p.graphic.attr?.({ opacity: 1 })
        const color = this._cloneGraphic(chart, p, lighter, animate, SIG_CLASS)
        const stripes = this._cloneGraphic(chart, p, `url(#${this.patternId})`, animate, SIG_CLASS)
        if (color) this.overlayEls.push(color)
        if (stripes) this.overlayEls.push(stripes)
      }
    },

    // Clone a point's graphic (path for rounded columns, rect otherwise) as a non-interactive overlay.
    // Appended to the series group → renders on top of the original (mirrors the old D3 append).
    _cloneGraphic(chart, p, fill, animate, className) {
      const g = p.graphic
      if (!g) return null
      const attrs = { fill, 'pointer-events': 'none', ...(className && { class: className }) }
      if (animate) attrs.opacity = 0
      const el = Object.hasOwn(g, 'd')
        ? chart.renderer.path().attr({ ...attrs, d: g.d || g.element?.getAttribute?.('d') })
        : chart.renderer.rect(g.x, g.y, g.width, g.height).attr(attrs)
      el.add(g.parentGroup)
      if (animate) el.animate({ opacity: 1 }, { duration: this.pointDuration })
      return el
    },

    // Custom legend (top-left): CI row + significance row. Replaces the standard legend while shown.
    _showLegend(chart, point, format, animate) {
      // zIndex 7 = same layer as the standard legend it replaces, BELOW the tooltip (z-index 8),
      // so the tooltip always renders on top instead of the legend overlapping it.
      const group = chart.renderer
        .g('moe-legend')
        .attr({ transform: 'translate(75, 25)', zIndex: 7, opacity: animate ? 0 : 1 })
        .add()

      if (format.showErrors) {
        const ci = getConfidenceInterval(point, this.config.percentDecimals, this._ciOptions()) || ''
        chart.renderer.rect(0, 0, 20, 14).attr({ fill: CI_BAND_COLOR }).add(group)
        this._legendText(chart, `${ci} Confidence Interval`, 25, 12).add(group)
      }

      if (format.showSignificance) {
        chart.renderer.rect(0, 18, 20, 14).attr({ fill: 'rgba(0, 0, 0, 0.5)' }).add(group)
        chart.renderer
          .rect(0, 18, 20, 14)
          .attr({ fill: `url(#${this.patternId})` })
          .add(group)
        this._legendText(chart, getConfidenceLevelText(this.config), 25, 30).add(group)
      }

      this.legendGroup = group
      if (animate) group.animate({ opacity: 1 }, { duration: this.pointDuration }) // legacy legend fade-in
    },

    _legendText(chart, text, x, y) {
      return chart.renderer
        .text(text, x, y)
        .attr({ fill: '#000000' })
        .css({ fontSize: '12px', fontFamily: LEGEND_FONT, cursor: 'default' })
    },

    _ciOptions() {
      const c = this.config
      return {
        qType: c.qType,
        viewMode: c.viewMode,
        answerType: c.answerType,
        ignorePercents: c.ignorePercents,
        confidenceLevel: c.confidenceLevel,
        axisType: c.axisType
      }
    }
  }
}

// Declarative delegation: attach point/series/chart events that call into the controller.
// Called ONLY by column/bar/dataLab leaf builders (columnConfig/barConfig/dataLabColumnsConfig),
// never by the shared calcColumnBarIngot. No-op when no controller is provided.
// Regular `function () {}` is required so `this` === the Highcharts point/series/chart.
export const wireMoEEvents = (options, props) => {
  const ctrl = props?.moeController
  if (!ctrl) return

  options.plotOptions = options.plotOptions || {}
  options.plotOptions.series = options.plotOptions.series || {}
  const series = options.plotOptions.series

  series.point = series.point || {}
  // Chained, not replaced: a builder may already have wired these, and spreading the object then
  // redefining the same keys drops whatever it wired without a word.
  const pointEvents = series.point.events || {}
  series.point.events = {
    ...pointEvents,
    mouseOver(...args) {
      pointEvents.mouseOver?.apply(this, args)
      ctrl.pointMouseOver(this)
    },
    mouseOut(...args) {
      pointEvents.mouseOut?.apply(this, args)
      ctrl.pointMouseOut(this)
    },
    click(...args) {
      pointEvents.click?.apply(this, args)
      ctrl.pointClick(this)
    }
  }

  series.events = {
    ...(series.events || {}),
    legendItemClick(event) {
      return ctrl.legendItemClick(this, event)
    }
  }

  options.chart = options.chart || {}
  options.chart.events = {
    ...(options.chart.events || {}),
    click() {
      ctrl.backgroundClick()
    },
    redraw() {
      ctrl.onRedraw(this)
    },
    load() {
      ctrl.onLoad(this)
    }
  }
}
